<template>
  <div class="hero-section">
    <div class="circles-container">
      <div class="rotating-circle"></div>
    </div>

    <div class="content">
      <div class="badges">
        <span class="badge">Photographers</span>
        <span class="badge">Studios</span>
        <span class="badge">Agencies</span>
      </div>
      <h1>Elevate Your Photography Business</h1>
      <h2 class="gradient-text">with Next-Gen 360° Technology</h2>
      <p class="hero-description">
        Transform your smartphone into a powerful 360° camera and offer premium
        immersive experiences to your clients — no specialized equipment
        required.
      </p>

      <div class="hero-cta">
        <a href="#partner-form">
          <button class="cta-secondary cta-primary">Become a Partner</button>
        </a>
        <a href="https://travvir.com/place/A03smB" target="_blank">
          <button class="cta-secondary">View Sample</button>
        </a>
      </div>
      <div class="scroll-indicator">
        <p>Scroll to learn more</p>
        <RiArrowDownSLine size="24" @click="handleScroll" />
      </div>
    </div>
    <div class="hero-media">
      <div class="phone-mockup">
        <div></div>
        <div
          class="screen"
          style="
            width: 100%;
            height: 100%;
            background-color: rgba(255, 255, 255, 0.2);
          "
        >
          <MuxVideo
            :blur="false"
            mux-id="xChyXZ1rvIj4n01QqwBmQgNyBvyefYu1IfqnyCWDpUl00"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { RiArrowDownSLine } from "@remixicon/vue";
import MuxVideo from "~/components/home/MuxVideo.vue";

const handleScroll = () => {
  const targetSection = document.querySelector(".benefits-section");
  if (targetSection) {
    targetSection.scrollIntoView({ behavior: "smooth" });
  }
};
</script>

<style lang="scss" scoped>
.hero-section {
  color: white;
  min-height: 100vh;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 0rem 4rem;

  @include respond-below(lg) {
    flex-direction: column;
    padding: 6rem 2rem;
  }

  @include respond-below(sm) {
    padding: 4rem 1.5rem;
    text-align: center;
  }

  .circles-container {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 0;
    overflow: hidden;
  }

  .rotating-circle {
    position: fixed;
    width: 400px;
    height: 400px;
    top: 50%;
    left: 100%;
    transform: translate(-100%, -50%);
    border-radius: 50%;
    filter: blur(120px);
    background: conic-gradient(
      rgba(44, 44, 84, 0.9) 0%,
      rgba(52, 52, 101, 0.85) 10%,
      rgba(63, 63, 119, 0.8) 20%,
      rgba(76, 76, 138, 0.85) 30%,
      rgba(89, 89, 157, 0.9) 40%,
      rgba(102, 102, 175, 0.85) 50%,
      rgba(115, 115, 194, 0.8) 60%,
      rgba(102, 102, 175, 0.85) 70%,
      rgba(89, 89, 157, 0.8) 80%,
      rgba(63, 63, 119, 0.85) 90%,
      rgba(44, 44, 84, 0.9) 100%
    );
    animation: rotate-stretch-pulse 20s linear infinite;

    @include respond-below(lg) {
      width: 450px;
      height: 450px;
    }

    @include respond-below(sm) {
      width: 350px;
      height: 350px;
      filter: blur(120px);
    }
  }

  @keyframes rotate-stretch-pulse {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
      width: 400px;
    }
    25% {
      transform: translate(-50%, -50%) rotate(90deg);
      width: 520px;
    }
    50% {
      transform: translate(-50%, -50%) rotate(180deg);
      width: 580px;
    }
    75% {
      transform: translate(-50%, -50%) rotate(270deg);
      width: 520px;
    }
    100% {
      transform: translate(-50%, -50%) rotate(350deg);
      width: 400px;
    }
  }

  .content {
    z-index: 3;
    max-width: 600px;

    @include respond-below(lg) {
      max-width: 100%;
      margin-bottom: 4rem;
    }
    @include respond-below(xs) {
      margin-bottom: 0rem;
    }

    .badges {
      display: flex;
      gap: 1rem;
      margin-bottom: 2rem;

      @include respond-below(sm) {
        justify-content: start;
        flex-wrap: wrap;
      }

      .badge {
        border: 1px solid rgba(255, 255, 255, 0.1);
        padding: 0.5rem 1rem;
        border-radius: 30px;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.6);
      }
    }
    @include respond-below(sm) {
      text-align: start;
    }

    h1 {
      font-size: 60px;
      font-weight: 600;
      line-height: 1.1;
      margin-bottom: 0.75rem;
      color: rgba(255, 255, 255, 0.9);

      @include respond-below(sm) {
        font-size: 10vw;
      }
    }

    .gradient-text {
      font-size: 40px;
      font-weight: 500;
      margin-top: 0;
      margin-bottom: 1.5rem;
      color: #fff9;
      text-align: left;

      @include respond-below(sm) {
        font-size: 8vw;
      }
    }

    .hero-description {
      font-size: 18px;
      line-height: 1.6;
      opacity: 0.9;
      margin-bottom: 2.5rem;
      max-width: 550px;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.4);

      @include respond-below(sm) {
        font-size: 16px;
        line-height: 1.5;
      }
    }

    .hero-cta {
      display: flex;
      gap: 1rem;
      margin-bottom: 4rem;
      @include respond-below(sm) {
        align-items: center;
      }
      button {
        padding: 1rem 2rem;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;

        @include respond-below(sm) {
          padding: 0.6rem 1rem;
        }
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        }

        &.cta-secondary {
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          &.cta-primary {
            background: rgb(13, 13, 13);
            border: none;
            color: white;
          }
        }
      }
    }

    .scroll-indicator {
      cursor: pointer;
      position: absolute;
      bottom: 10%;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      opacity: 0.7;
      @include respond-below(lg) {
        bottom: 0%;
      }
      p {
        font-size: 14px;
        margin-bottom: 0.5rem;
        color: rgba(255, 255, 255, 0.4);
      }

      svg {
        animation: bounce 2s infinite;
      }
    }
  }

  .hero-media {
    position: relative;
    height: 45vh;
    width: calc(45vh * 1.72);
    z-index: 20;
    margin-bottom: 4rem;
    margin-right: 4rem;
    @include respond-below(sm) {
      height: 400px;
      width: 100%;
      margin-bottom: 0rem;
      margin-right: 0rem;
    }
    .phone-mockup {
      position: relative;
      height: 100%;
      width: 100%;
      border-radius: 40px;
      overflow: hidden;

      .screen {
        position: absolute;
        inset: 0;
        opacity: 0.8;
        border-radius: 20px;
        &:after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
        }
      }
    }
  }
}
</style>
